import { lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { arrivedByPasswordLink } from './lib/recovery';
import { SiteTextProvider } from './lib/text';
import { MediaProvider } from './lib/media';
import EditModeBar from './components/EditModeBar';
import Header from './components/Header';
import Footer from './components/Footer';
import MembersOnly from './components/MembersOnly';
import Home from './pages/Home';

// Home ships in the main bundle (it's the landing page and its LCP matters);
// every other route loads on demand so first paint isn't paying for the
// admin panels, portal, or pages the visitor never opens.
const About = lazy(() => import('./pages/About'));
const Board = lazy(() => import('./pages/Board'));
const Foundation = lazy(() => import('./pages/Foundation'));
const Bylaws = lazy(() => import('./pages/Bylaws'));
const Membership = lazy(() => import('./pages/Membership'));
const Events = lazy(() => import('./pages/Events'));
const News = lazy(() => import('./pages/News'));
const Sponsors = lazy(() => import('./pages/Sponsors'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Members = lazy(() => import('./pages/Members'));
const Admin = lazy(() => import('./pages/Admin'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Classes = lazy(() => import('./pages/Classes'));
const QandA = lazy(() => import('./pages/QandA'));
const Videos = lazy(() => import('./pages/Videos'));
const DirectorGuide = lazy(() => import('./pages/DirectorGuide'));
const Programs = lazy(() => import('./pages/Programs'));
const Hubs = lazy(() => import('./pages/Hubs'));
const PaymentComplete = lazy(() => import('./pages/PaymentComplete'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Dark placeholder the height of a page banner, so the header doesn't sit on
// a white flash while a route chunk downloads (typically <100ms on repeat
// visits, since chunks are cached).
function RouteFallback() {
  return <div className="bg-ink min-h-[60vh]" aria-busy="true" aria-label="Loading page" />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    // Braced body on purpose: a concise arrow would return scrollTo's result,
    // and browser extensions that wrap window.scrollTo can make that a truthy
    // non-function — which React would then invoke as an effect cleanup on the
    // next navigation and crash ("TypeError: r is not a function").
    window.scrollTo(0, 0);
    // Move keyboard focus to the new page's content (skip the initial load so
    // the browser's default focus behavior is preserved).
    if (!firstRender.current) {
      try {
        document.getElementById('main')?.focus({ preventScroll: true });
      } catch {
        /* focus is an enhancement; never let it break navigation */
      }
    }
    firstRender.current = false;
    // A hash names a section (e.g. /members#boards from the homepage's edit
    // mode). The admin panels render only after the session check, so keep
    // looking for the target for a few seconds rather than only once.
    if (hash.length < 2) return;
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'start' });
      if (el || ++tries > 40) window.clearInterval(timer);
    }, 100);
    return () => window.clearInterval(timer);
  }, [pathname, hash]);
  return null;
}

function RecoveryRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    // Password-reset emails land on the site root (the allow-listed redirect);
    // supabase-js consumes the token from the URL hash and fires this event.
    // Send the member straight to the portal with the "Set your password"
    // card first (?setpw=1). The event can fire before this effect has
    // subscribed, so when the page load itself came from a password link,
    // also wait for the session and go there regardless. Whichever path
    // fires first wins; the portal strips ?setpw=1 once it has read it.
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      navigate('/members?setpw=1', { replace: true });
    };
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') go();
    });
    if (arrivedByPasswordLink) {
      // getSession waits for supabase-js to finish reading the hash.
      supabase.auth.getSession().then(({ data: s }) => {
        if (s.session) go();
      });
    }
    return () => data.subscription.unsubscribe();
  }, [navigate]);
  return null;
}

function OldHubsAddress() {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace(/^\/admin\/hubs/, '/hubs')} replace />;
}

function CanonicalUrl() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Origin-relative so the tags stay correct after the faemse.org cutover.
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    // No trailing slash, to match the page files scripts/route-pages.mjs
    // writes (GitHub Pages may answer /sponsors at /sponsors/).
    const path = pathname.replace(/\/+$/, '') || '/';
    const href = `${window.location.origin}${base}${path}`;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = href;
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', href);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <SiteTextProvider>
    <MediaProvider>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-white focus:text-ink focus:font-bold focus:px-5 focus:py-3 focus:rounded-xl focus:shadow-xl"
      >
        Skip to content
      </a>
      <ScrollToTop />
      <CanonicalUrl />
      <RecoveryRedirect />
      <Header />
      <main id="main" tabIndex={-1} className="outline-none">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/board" element={<Board />} />
            <Route path="/foundation" element={<Foundation />} />
            <Route path="/bylaws" element={<Bylaws />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/events" element={<Events />} />
            {/* Public (board decision 2026-10-20): news is how visitors find
                reasons to join. Shows real posts only. */}
            <Route path="/news" element={<News />} />
            {/* Members only (board decision 2026-10-13): visitors get the
                banner and a sign-in / join notice. Each head matches the
                page's own PageHead. */}
            {/* The members' Resources page moved into the portal (board,
                2026-10-20); old links land on its Resources tab. */}
            <Route path="/resources" element={<Navigate to="/members/resources" replace />} />
            <Route path="/sponsors" element={<Sponsors />} />
            <Route
              path="/jobs"
              element={
                <MembersOnly
                  what="The job board"
                  head={{ id: 'jobs', eyebrow: 'Careers in EMS education', title: 'Job board', sub: 'Openings for instructors, coordinators, and program directors across Florida — posted by the board as we hear of them.' }}
                >
                  <Jobs />
                </MembersOnly>
              }
            />
            <Route path="/classes" element={<Classes />} />
            <Route
              path="/qa"
              element={
                <MembersOnly
                  what="The Q&A archive"
                  head={{ id: 'qa', eyebrow: 'The archive', title: 'Questions & answers', sub: 'Real questions from Florida EMS educators, answered once and kept — so the knowledge stops evaporating with the listserv.' }}
                >
                  <QandA />
                </MembersOnly>
              }
            />
            <Route path="/videos" element={<Videos />} />
            <Route path="/program-directors" element={<DirectorGuide />} />
            <Route path="/programs" element={<Programs />} />
            {/* Current members and admins; the page and the database both enforce it. */}
            <Route path="/hubs/:hub?/:section?/:sub?" element={<Hubs />} />
            {/* The hubs' first address, when they were admins only (Oct 10-13, 2026). */}
            {/* Admins only (the page checks); the header shows it to admins. */}
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/hubs/*" element={<OldHubsAddress />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            {/* The member portal and its tabs: /members, /members/resources,
                /members/jobs, /members/account, /members/board (admins). */}
            <Route path="/members/:tab?" element={<Members />} />
            <Route path="/payment-complete" element={<PaymentComplete />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      {/* Admin-only; renders nothing for everyone else. */}
      <EditModeBar />
    </MediaProvider>
    </SiteTextProvider>
  );
}
